// Background upload of the raw walkthrough video for a room scan session.
//
// Flow:
//   1. Upload the Blob to room-scans/{user}/{session}/source.{mp4|webm}
//   2. Generate a 7-year signed URL
//   3. POST it to the attach-video-to-session Edge Function
//   4. On any failure, retry up to 3 attempts with exponential backoff
//      (1s, 4s, 16s).
//   5. If all retries fail, record the session in
//      localStorage['pending_video_uploads'] so the next app open can
//      surface it. We can't replay the upload after a reload (the Blob
//      isn't persisted), so the sweep is diagnostic only.
//
// Caller fires this fire-and-forget after the review screen has rendered —
// the user is reviewing items while the upload progresses in the background.

import { supabase } from '../supabase'

const PENDING_KEY = 'pending_video_uploads'
const RETRY_DELAYS_MS = [1000, 4000, 16000]
const SIGNED_URL_EXPIRY_SECONDS = 60 * 60 * 24 * 365 * 7 // 7 years

function recordPending(entry) {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    const arr = raw ? JSON.parse(raw) : []
    if (!Array.isArray(arr)) {
      localStorage.setItem(PENDING_KEY, JSON.stringify([entry]))
      return
    }
    arr.push(entry)
    localStorage.setItem(PENDING_KEY, JSON.stringify(arr))
  } catch (err) {
    console.error('[videoBackgroundUpload] localStorage write failed', err)
  }
}

async function attempt(blob, sessionId, userId) {
  const ext = (blob?.type || '').includes('mp4') ? 'mp4' : 'webm'
  const path = `${userId}/${sessionId}/source.${ext}`

  const { error: upErr } = await supabase.storage
    .from('room-scans')
    .upload(path, blob, { contentType: blob?.type || `video/${ext}`, upsert: true })
  if (upErr) throw new Error(`upload: ${upErr.message}`)

  const { data: signed, error: signErr } = await supabase.storage
    .from('room-scans')
    .createSignedUrl(path, SIGNED_URL_EXPIRY_SECONDS)
  if (signErr || !signed?.signedUrl) {
    throw new Error(`signed-url: ${signErr?.message || 'unknown'}`)
  }

  const { error: fnErr } = await supabase.functions.invoke('attach-video-to-session', {
    body: { session_id: sessionId, video_url: signed.signedUrl },
  })
  if (fnErr) throw new Error(`attach: ${fnErr.message || fnErr}`)
}

/**
 * Fire-and-forget. Resolves only after success or final failure; callers
 * are not expected to await it.
 */
export async function uploadVideoInBackground(blob, sessionId, userId) {
  if (!blob || !sessionId || !userId) {
    console.error('[videoBackgroundUpload] missing inputs', { hasBlob: !!blob, sessionId, userId })
    return
  }
  for (let i = 0; i < RETRY_DELAYS_MS.length; i++) {
    try {
      await attempt(blob, sessionId, userId)
      console.log('[videoBackgroundUpload] success', { sessionId, attempt: i + 1 })
      return
    } catch (err) {
      console.error(`[videoBackgroundUpload] attempt ${i + 1} failed`, err?.message || err)
      if (i < RETRY_DELAYS_MS.length - 1) {
        await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[i]))
      }
    }
  }
  recordPending({
    session_id: sessionId,
    user_id: userId,
    blob_size: blob.size,
    blob_type: blob.type || null,
    failed_at: new Date().toISOString(),
  })
}

/**
 * Sweep called on app mount. Surfaces the metadata of any failed uploads
 * so we can see them in console (and later, the UI). Does NOT replay
 * the upload — the original Blob is not persisted across reloads.
 *
 * Returns the pending entries for the caller to inspect / display.
 */
export function sweepPendingVideoUploads() {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr) || arr.length === 0) return []
    console.warn(`[videoBackgroundUpload] ${arr.length} pending video upload(s) recorded from previous sessions`, arr)
    return arr
  } catch (err) {
    console.error('[videoBackgroundUpload] sweep failed', err)
    return []
  }
}

/**
 * Clear one or all pending entries. Used by the sweep UI when the user
 * acknowledges them.
 */
export function clearPendingVideoUploads(sessionId) {
  try {
    if (!sessionId) {
      localStorage.removeItem(PENDING_KEY)
      return
    }
    const raw = localStorage.getItem(PENDING_KEY)
    if (!raw) return
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return
    const remaining = arr.filter(e => e?.session_id !== sessionId)
    if (remaining.length === 0) {
      localStorage.removeItem(PENDING_KEY)
    } else {
      localStorage.setItem(PENDING_KEY, JSON.stringify(remaining))
    }
  } catch (err) {
    console.error('[videoBackgroundUpload] clear failed', err)
  }
}
